// Configuração do banco de dados
const DB_NAME = 'iptvPlayerDB';
const DB_VERSION = 3; // Incrementado para adicionar EPG stores
const STORE_NAME = 'channels';
const ORGANIZED_STORE_NAME = 'organizedChannels';
const METADATA_STORE_NAME = 'metadata';
const EPG_CHANNELS_STORE = 'epg_channels';
const EPG_PROGRAMS_STORE = 'epg_programs';

class DBService {
  constructor() {
    this.db = null;
    this.initDB();
  }

  // Inicializa o banco de dados
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Erro ao abrir o banco de dados');
        reject(request.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'chunkIndex' });
        }
        if (!db.objectStoreNames.contains(ORGANIZED_STORE_NAME)) {
          db.createObjectStore(ORGANIZED_STORE_NAME, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(METADATA_STORE_NAME)) {
          db.createObjectStore(METADATA_STORE_NAME, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(EPG_CHANNELS_STORE)) {
          db.createObjectStore(EPG_CHANNELS_STORE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(EPG_PROGRAMS_STORE)) {
          const programStore = db.createObjectStore(EPG_PROGRAMS_STORE, { keyPath: 'channelId' });
          // Não precisamos de índice secundário por enquanto pois a chave primária já é o channelId
        }
      };
    });
  }

  // Salva um chunk de canais
  async saveChannelChunk(chunkIndex, channels) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const request = store.put({
        chunkIndex,
        channels,
        timestamp: Date.now()
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Carrega um chunk específico
  async loadChannelChunk(chunkIndex) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(chunkIndex);

      request.onsuccess = () => {
        resolve(request.result ? request.result.channels : null);
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Limpa todos os chunks
  async clearAllChunks() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Verifica se um chunk existe
  async hasChunk(chunkIndex) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count(chunkIndex);

      request.onsuccess = () => resolve(request.result > 0);
      request.onerror = () => reject(request.error);
    });
  }

  // Salva a estrutura completa de canais organizados por qualidade
  async saveOrganizedChannels(organizedChannels, groups) {
    if (!this.db) await this.initDB();

    return new Promise(async (resolve, reject) => {
      try {
        // Converter Map para objeto serializável se necessário
        const channelsData = organizedChannels instanceof Map
          ? Object.fromEntries(organizedChannels)
          : organizedChannels;

        // Converter para array de objetos para salvar no IndexedDB
        const channelsArray = Object.entries(channelsData).map(([cleanName, channelGroup]) => ({
          id: cleanName,
          cleanName,
          group: channelGroup.group,
          logo: channelGroup.logo,
          versions: channelGroup.versions
        }));

        const transaction = this.db.transaction([ORGANIZED_STORE_NAME, METADATA_STORE_NAME], 'readwrite');
        const organizedStore = transaction.objectStore(ORGANIZED_STORE_NAME);
        const metadataStore = transaction.objectStore(METADATA_STORE_NAME);

        // Limpar dados antigos
        await new Promise((res, rej) => {
          const clearRequest = organizedStore.clear();
          clearRequest.onsuccess = () => res();
          clearRequest.onerror = () => rej(clearRequest.error);
        });

        // Salvar cada canal organizado
        for (const channel of channelsArray) {
          await new Promise((res, rej) => {
            const request = organizedStore.put(channel);
            request.onsuccess = () => res();
            request.onerror = () => rej(request.error);
          });
        }

        // Salvar grupos
        await new Promise((res, rej) => {
          const groupsRequest = metadataStore.put({
            key: 'groups',
            value: Array.isArray(groups) ? groups : Array.from(groups),
            timestamp: Date.now()
          });
          groupsRequest.onsuccess = () => res();
          groupsRequest.onerror = () => rej(groupsRequest.error);
        });

        // Salvar timestamp de última atualização
        await new Promise((res, rej) => {
          const timestampRequest = metadataStore.put({
            key: 'lastUpdate',
            value: Date.now(),
            timestamp: Date.now()
          });
          timestampRequest.onsuccess = () => res();
          timestampRequest.onerror = () => rej(timestampRequest.error);
        });

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  // Carrega a estrutura completa de canais organizados
  async loadOrganizedChannels() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([ORGANIZED_STORE_NAME, METADATA_STORE_NAME], 'readonly');
      const organizedStore = transaction.objectStore(ORGANIZED_STORE_NAME);
      const metadataStore = transaction.objectStore(METADATA_STORE_NAME);

      const channelsRequest = organizedStore.getAll();
      const groupsRequest = metadataStore.get('groups');
      const timestampRequest = metadataStore.get('lastUpdate');

      let channelsData = null;
      let groups = null;
      let timestamp = null;

      channelsRequest.onsuccess = () => {
        const channels = channelsRequest.result;
        if (channels && channels.length > 0) {
          // Converter array de volta para objeto
          channelsData = {};
          channels.forEach(channel => {
            channelsData[channel.cleanName] = {
              cleanName: channel.cleanName,
              group: channel.group,
              logo: channel.logo,
              versions: channel.versions
            };
          });
        }
      };

      groupsRequest.onsuccess = () => {
        if (groupsRequest.result) {
          groups = groupsRequest.result.value;
        }
      };

      timestampRequest.onsuccess = () => {
        if (timestampRequest.result) {
          timestamp = timestampRequest.result.value;
        }
      };

      transaction.oncomplete = () => {
        if (channelsData) {
          resolve({
            organizedChannels: channelsData,
            groups: groups ? new Set(groups) : new Set(),
            timestamp
          });
        } else {
          resolve(null);
        }
      };

      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Verifica se existe dados salvos no banco
  async hasStoredData() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([ORGANIZED_STORE_NAME], 'readonly');
      const store = transaction.objectStore(ORGANIZED_STORE_NAME);
      const request = store.count();

      request.onsuccess = () => {
        resolve(request.result > 0);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Limpa os dados organizados
  async clearOrganizedChannels() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([ORGANIZED_STORE_NAME, METADATA_STORE_NAME], 'readwrite');
      const organizedStore = transaction.objectStore(ORGANIZED_STORE_NAME);
      const metadataStore = transaction.objectStore(METADATA_STORE_NAME);

      const clearOrganized = organizedStore.clear();
      const clearMetadata = metadataStore.clear();

      let organizedDone = false;
      let metadataDone = false;

      const checkComplete = () => {
        if (organizedDone && metadataDone) {
          resolve();
        }
      };

      clearOrganized.onsuccess = () => {
        organizedDone = true;
        checkComplete();
      };

      clearMetadata.onsuccess = () => {
        metadataDone = true;
        checkComplete();
      };
      clearOrganized.onerror = () => reject(clearOrganized.error);
      clearMetadata.onerror = () => reject(clearMetadata.error);
    });
  }

  // Salva dados do EPG
  async saveEPG(data) {
    if (!this.db) await this.initDB();

    return new Promise(async (resolve, reject) => {
      try {
        const transaction = this.db.transaction([EPG_CHANNELS_STORE, EPG_PROGRAMS_STORE, METADATA_STORE_NAME], 'readwrite');
        const channelsStore = transaction.objectStore(EPG_CHANNELS_STORE);
        const programsStore = transaction.objectStore(EPG_PROGRAMS_STORE);
        const metadataStore = transaction.objectStore(METADATA_STORE_NAME);

        // Limpar dados antigos
        await Promise.all([
          new Promise((res, rej) => {
            const req = channelsStore.clear();
            req.onsuccess = res;
            req.onerror = rej;
          }),
          new Promise((res, rej) => {
            const req = programsStore.clear();
            req.onsuccess = res;
            req.onerror = rej;
          })
        ]);

        // Salvar canais
        for (const channel of data.channels) {
          channelsStore.put(channel);
        }

        // Salvar programas (agrupados por canal)
        // data.programs é um objeto { channelId: [programs...] }
        const channelIds = Object.keys(data.programs);

        // Processar em chunks para não bloquear a thread
        const CHUNK_SIZE = 50;
        for (let i = 0; i < channelIds.length; i += CHUNK_SIZE) {
          const chunk = channelIds.slice(i, i + CHUNK_SIZE);

          await Promise.all(chunk.map(channelId => {
            return new Promise((res, rej) => {
              // Salvar apenas se tiver programas
              if (data.programs[channelId] && data.programs[channelId].length > 0) {
                const req = programsStore.put({
                  channelId: channelId,
                  programs: data.programs[channelId]
                });
                req.onsuccess = res;
                req.onerror = rej;
              } else {
                res();
              }
            });
          }));
        }

        // Salvar timestamp
        metadataStore.put({
          key: 'epg_last_update',
          value: Date.now(),
          timestamp: Date.now()
        });

        resolve();
      } catch (error) {
        console.error('Erro ao salvar EPG:', error);
        reject(error);
      }
    });
  }

  // Carrega dados do EPG
  async loadEPG() {
    if (!this.db) await this.initDB();

    return new Promise(async (resolve, reject) => {
      try {
        const transaction = this.db.transaction([EPG_CHANNELS_STORE, EPG_PROGRAMS_STORE, METADATA_STORE_NAME], 'readonly');
        const channelsStore = transaction.objectStore(EPG_CHANNELS_STORE);
        const programsStore = transaction.objectStore(EPG_PROGRAMS_STORE);
        const metadataStore = transaction.objectStore(METADATA_STORE_NAME);

        // Verificar timestamp
        const timestampReq = metadataStore.get('epg_last_update');

        const timestamp = await new Promise((res) => {
          timestampReq.onsuccess = () => res(timestampReq.result ? timestampReq.result.value : null);
          timestampReq.onerror = () => res(null);
        });

        if (!timestamp) {
          resolve(null);
          return;
        }

        // Carregar canais
        const channelsReq = channelsStore.getAll();
        const channels = await new Promise((res, rej) => {
          channelsReq.onsuccess = () => res(channelsReq.result);
          channelsReq.onerror = () => rej(channelsReq.error);
        });

        // Carregar programas
        const programsReq = programsStore.getAll();
        const programsList = await new Promise((res, rej) => {
          programsReq.onsuccess = () => res(programsReq.result);
          programsReq.onerror = () => rej(programsReq.error);
        });

        // Reconstruir objeto de programas
        const programs = {};
        programsList.forEach(item => {
          programs[item.channelId] = item.programs;
        });

        resolve({
          channels,
          programs,
          timestamp
        });
      } catch (error) {
        console.error('Erro ao carregar EPG:', error);
        reject(error);
      }
    });
  }
}

export default new DBService(); 